/**
 * Status / tag -> FSD `<Badge tone>` mapping.
 *
 * Two strict palettes (mirrors `@shared/ui/Badge`):
 *   - Clinical Status (semantic, colour = meaning):
 *     'critical' | 'high' | 'moderate' | 'normal' | 'pending' | 'inactive'
 *   - Category Tags (neutral, colour only differentiates):
 *     'slate' | 'sage' | 'indigo' | 'stone' | 'wine' | 'graphite'
 *
 * Mappings calibrated 2026-05-05 per medical-app design audit:
 *   - "Status" values that carry severity / outcome semantics use
 *     the Clinical Status palette.
 *   - "Tag" / source / demographic values that just differentiate
 *     things use Category Tags (no inherent severity implied).
 *
 * Returns the FSD tone name as a string. Consumers (legacy `<Badge
 * colorType>`, FSD `<Badge tone>`) accept these directly.
 */
import { ASSESSMENT_STATUSES } from '@entities/assessment';
import { JOURNEY_LIFECYCLE, DEFINITION_STATUS } from '@entities/journey';
import { ORDER_STATUSES } from '@entities/order';
import { RESOURCE_STATUSES } from '@entities/resource';
import {
  BILLING_CLAIMS_STATUSES,
  CLAIM_STATUS_SELECTOR_STATUSES,
  CLAIMS_STATUSES,
} from '@entities/claim';
import { CLAIM_REPORTS_STATUSES } from '@shared/lib/claimReportStatuses';
import { CUSTOM_TAGS_STATUSES } from '_legacy/components/Shared/CustomTags';
import { ALERTS_TYPE, ENTITY_SOURCE_TYPE } from '@entities/alert';
import { BOT_UPLOADS_STATUSES } from '@entities/bot-activity';
import { BHI_STATUSES } from '@entities/program';
import { PATIENT_ENGAGEMENT_STATUSES } from '@entities/patient';
import { PROVIDER_DEFAULT_STATUSES } from '@entities/provider';
import { GENDERS } from '@entities/population-health';

const StatusColor = {
  // ---- Order ----
  [ORDER_STATUSES.shipped]: 'normal', // delivered, success
  [ORDER_STATUSES.processing]: 'pending', // in flight
  [ORDER_STATUSES.pending]: 'pending',
  [ORDER_STATUSES.sending]: 'pending',
  [ORDER_STATUSES.delayed]: 'moderate', // caution
  [ORDER_STATUSES.onHold]: 'moderate', // paused, attention
  [ORDER_STATUSES.canceled]: 'inactive', // terminal not-success

  // ---- Assessment ----
  [ASSESSMENT_STATUSES.pendingReview]: 'high', // action required
  // In-flight like `processing`, not action-required — matches the
  // lifecycle grouping of the status filter (customer, 2026-08-24).
  [ASSESSMENT_STATUSES.partialProcessed]: 'pending',
  [ASSESSMENT_STATUSES.processing]: 'pending', // was 'red' — semantically wrong; in flight, not bad
  [ASSESSMENT_STATUSES.completed]: 'normal', // success
  [ASSESSMENT_STATUSES.inProgress]: 'pending',
  [ASSESSMENT_STATUSES.invited]: 'pending', // awaiting response
  // Patient opened the link but hasn't accepted terms yet — still
  // awaiting patient action, same operational weight as invited.
  [ASSESSMENT_STATUSES.staled]: 'pending',
  [ASSESSMENT_STATUSES.scheduled]: 'pending',
  [ASSESSMENT_STATUSES.created]: 'inactive', // exists, no action
  [ASSESSMENT_STATUSES.eTransfer]: 'normal', // transfer success
  [ASSESSMENT_STATUSES.canceled]: 'inactive',
  // No-show is terminal but operationally important — it usually
  // demands a follow-up call / reschedule, unlike a plain cancel.
  // Tag as `high` (action required) so the row stands out.
  [ASSESSMENT_STATUSES.noShow]: 'high',
  // Separate backend `noShowStatus` column — value 'none' means
  // not flagged as no-show. Render as a quiet neutral badge so
  // the column reads as "no concern here" without screaming.
  none: 'inactive',
  // `declined` shares the noShowStatus column with `noShow` and the
  // same follow-up semantics (appointment didn't happen) -> same tone.
  declined: 'high',

  // ---- Resource ----
  [RESOURCE_STATUSES.invited]: 'pending',
  [RESOURCE_STATUSES.engaged]: 'normal', // active engagement
  [RESOURCE_STATUSES.scheduled]: 'pending',

  // ---- Claim ----
  [CLAIMS_STATUSES.draft]: 'inactive', // not yet active
  [CLAIMS_STATUSES.notCreated]: 'pending',
  [CLAIMS_STATUSES.completed]: 'normal',

  // ---- Billing claim status (severity-based) ----
  [BILLING_CLAIMS_STATUSES.billed]: 'pending', // awaiting payment
  [BILLING_CLAIMS_STATUSES.readyToBill]: 'high', // ready for action
  [BILLING_CLAIMS_STATUSES.icdIssue]: 'critical', // problem, needs fix
  [BILLING_CLAIMS_STATUSES.insuranceIssue]: 'critical',
  [BILLING_CLAIMS_STATUSES.appointmentTypeIssue]: 'critical',
  [BILLING_CLAIMS_STATUSES.hold]: 'moderate', // paused
  [BILLING_CLAIMS_STATUSES.appointmentCancelled]: 'inactive', // terminal cancelled

  // ---- Claim Reports ----
  [CLAIM_REPORTS_STATUSES.reported]: 'normal',
  [CLAIM_REPORTS_STATUSES.reportable]: 'pending', // eligible, not yet done

  // ---- Alerts ----
  [ALERTS_TYPE.critical]: 'critical',
  [ALERTS_TYPE.nonCritical]: 'inactive',

  // ---- Entity Source — these distinguish CATEGORY of source, not
  // severity. Use Category Tags so they read as neutral labels. ----
  [ENTITY_SOURCE_TYPE.program]: 'sage',
  [ENTITY_SOURCE_TYPE.assessment]: 'indigo',
  [ENTITY_SOURCE_TYPE.resource]: 'slate',
  [ENTITY_SOURCE_TYPE.intellyChart]: 'stone',
  [ENTITY_SOURCE_TYPE.bhiWebhook]: 'wine',

  // ---- Bot Uploads ----
  [BOT_UPLOADS_STATUSES.success]: 'normal',
  [BOT_UPLOADS_STATUSES.error]: 'critical',
  // Stated rather than inherited: `pending` used to resolve only
  // because ORDER_STATUSES.pending is the same string further down
  // this map, so reordering or renaming that entry would have quietly
  // greyed out every pending bot upload.
  [BOT_UPLOADS_STATUSES.pending]: 'pending',

  // ---- Claim Status Selector (audited; was largely arbitrary) ----
  [CLAIM_STATUS_SELECTOR_STATUSES.patientResponsibility]: 'moderate', // resolved with notes
  [CLAIM_STATUS_SELECTOR_STATUSES.inProcess]: 'pending',
  [CLAIM_STATUS_SELECTOR_STATUSES.paid]: 'normal', // was 'orange' — paid is good
  [CLAIM_STATUS_SELECTOR_STATUSES.partiallyPaid]: 'moderate', // partial = caution
  [CLAIM_STATUS_SELECTOR_STATUSES.denied]: 'critical', // was 'grey' — denied is bad
  [CLAIM_STATUS_SELECTOR_STATUSES.notBillable]: 'inactive',
  [CLAIM_STATUS_SELECTOR_STATUSES.resubmitted]: 'pending', // awaiting new outcome

  // ---- Genders (demographic, not severity) — Category Tags ----
  [GENDERS.female]: 'wine',
  [GENDERS.male]: 'indigo',

  // ---- BHI ----
  [BHI_STATUSES.active]: 'normal',
  [BHI_STATUSES.inactive]: 'inactive',

  // ---- Patient Engagement master flag ----
  // Disabled means the patient receives no outreach at all — that is
  // an operationally loud state, so it gets the critical tone (dev
  // lineage used green/red for the same pair).
  [PATIENT_ENGAGEMENT_STATUSES.enabled]: 'normal',
  [PATIENT_ENGAGEMENT_STATUSES.disabled]: 'critical',

  // ---- Program journeys (lifecycle) — same tones as the journey
  // drawer's getJourneyLifecycleTone, so the list and the detail agree. ----
  [JOURNEY_LIFECYCLE.running]: 'pending', // in flight
  [JOURNEY_LIFECYCLE.paused]: 'inactive', // neutral hold
  [JOURNEY_LIFECYCLE.failed]: 'critical', // terminal failure
  // `completed` is already mapped to 'normal' by the assessment block
  // above (same string, same meaning).

  // ---- Program templates (definition status) — same tones as
  // getDefinitionStatusTone in the journey entity. `draft` shares its
  // string with the claims draft above and the same 'inactive' tone. ----
  [DEFINITION_STATUS.published]: 'normal',
  [DEFINITION_STATUS.archived]: 'inactive',

  // ---- Providers ----
  // 'Default' marks the account's default provider — a positive,
  // confirmed state (dev lineage used the green badge for the same
  // value), so it gets the success tone.
  [PROVIDER_DEFAULT_STATUSES.default]: 'normal',
};

const getStatusBadge = status => StatusColor[status];

export default getStatusBadge;

const TagsColor = {
  // ---- Custom account tags — neutral labels ----
  [CUSTOM_TAGS_STATUSES['ISP']]: 'sage',
  [CUSTOM_TAGS_STATUSES['License']]: 'indigo',
  [CUSTOM_TAGS_STATUSES['Orders Only']]: 'slate',
  [CUSTOM_TAGS_STATUSES['Trial']]: 'stone',
  [CUSTOM_TAGS_STATUSES['Demo']]: 'wine',
  [CUSTOM_TAGS_STATUSES['Other']]: 'graphite',
  // The "Inactive" tag carries semantic — terminal not-active —
  // so it borrows from the Clinical Status palette.
  [CUSTOM_TAGS_STATUSES['Inactive']]: 'inactive',

  // ---- Patient flag tags — neutral labels ----
  hasClaim: 'stone',
  hasProgram: 'sage',
  hasIntellyChart: 'indigo',
  instant: 'wine',

  // ---- Trial arms (research) — neutral labels ----
  control: 'slate',
  experimental: 'indigo',

  // ---- Provider default contexts (providers table Default column) —
  // category chips, color only differentiates the contexts ----
  defaultPdf: 'stone',
  defaultClaims: 'sage',
  defaultSurveys: 'indigo',
};

export const getTagsColor = status => TagsColor[status];
